import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Shield, Users, Database, Lock, ChevronLeft, Workflow, Scale, Table, Key, Eye, EyeOff, GraduationCap, Star, Award, Building2, Briefcase, HeadphonesIcon, Megaphone } from 'lucide-react';
// ---- Local visual primitives matched to Treasury aesthetic ----
const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-[#D4AF37]/70 tracking-[0.35em] text-[10px] uppercase font-light" data-id="element-7630">
    {children}
  </p>;
const SectionHeader = ({
  num,
  eyebrow,
  title,
  intro
}: {
  num: string;
  eyebrow: string;
  title: React.ReactNode;
  intro?: string;
}) => <div className="flex items-start gap-6 mb-12" data-id="element-7631">
    <span className="font-serif text-5xl sm:text-6xl text-[#D4AF37]/30 leading-none shrink-0" data-id="element-7632">
      {num}
    </span>
    <div data-id="element-7633">
      <Eyebrow data-id="element-7634">{eyebrow}</Eyebrow>
      <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#FDFBF7] mt-3 mb-4 leading-tight" data-id="element-7635">
        {title}
      </h2>
      <div className="w-12 h-px bg-[#D4AF37]/40 mb-4" data-id="element-7636" />
      {intro && <p className="text-[#FDFBF7]/65 text-sm sm:text-base font-light leading-relaxed max-w-3xl" data-id="element-7637">
          {intro}
        </p>}
    </div>
  </div>;
const Card = ({
  children,
  accent = false
}: {
  children: React.ReactNode;
  accent?: boolean;
}) => <div className={`relative border ${accent ? 'border-[#D4AF37]/45' : 'border-[#D4AF37]/15'} bg-gradient-to-b from-[#D4AF37]/[0.04] to-transparent p-5 sm:p-6 hover:border-[#D4AF37]/40 transition-colors`} data-id="element-7638">
    <span className="absolute -top-px left-5 right-5 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/55 to-transparent" data-id="element-7639" />
    {children}
  </div>;
// =====================================================================
// 1. USER ROLES
// =====================================================================
const ROLES = [{
  icon: Crown,
  code: 'treasury_admin',
  label: 'Treasury Admin',
  tier: 'Super',
  scope: 'Full system control.',
  can: ['Manage all members, collections, artifacts, rewards, events', 'Issue and revoke honours, ranks, founder flags', 'Edit business rules, supply caps, drop schedule', 'Access audit log and operational metrics']
}, {
  icon: Award,
  code: 'court_admin',
  label: 'Court Admin / Curator',
  tier: 'Staff',
  scope: 'Curates the world.',
  can: ['Create / edit collections and artifacts', 'Schedule daily packets and Treasury Nights', 'Define rarities, sponsor attribution', 'Approve trades flagged for review']
}, {
  icon: Megaphone,
  code: 'event_operator',
  label: 'Event Operator',
  tier: 'Staff',
  scope: 'Runs the live event surface.',
  can: ['Generate event codes (scan & Treasury Night)', 'Issue rewards from on-site Darbar Sessions', 'Confirm in-person attendance', 'Reset / re-issue single-use codes']
}, {
  icon: Star,
  code: 'misldar',
  label: 'Misldar',
  tier: 'Member-Leader',
  scope: 'Leads a Misl (clan).',
  can: ['Create the Misl, set name and seal', 'Invite, approve, and remove Misl members', 'Set Misl rules and rituals', 'View Misl-level XP and progress']
}, {
  icon: Crown,
  code: 'sardar',
  label: 'Sardar / Knight',
  tier: 'Member',
  scope: 'Knighted court member.',
  can: ['Open daily packets, build collections', 'Initiate and accept trades', 'Redeem rewards and event codes', 'Join one Misl at a time']
}, {
  icon: Shield,
  code: 'sevadar',
  label: 'Sevadar',
  tier: 'Member-Entry',
  scope: 'Pre-knighted member.',
  can: ['Open daily packets', 'Collect artifacts', 'Cannot trade until knighted', 'Cannot join a Misl until knighted']
}, {
  icon: Briefcase,
  code: 'sponsor_admin',
  label: 'Sponsor Admin',
  tier: 'Partner',
  scope: 'Corporate / patron sponsoring a collection.',
  can: ['View sponsored collection performance', 'See impressions, unlocks, reach', 'Cannot edit collections', 'Receive monthly sponsor reports']
}, {
  icon: GraduationCap,
  code: 'student_admin',
  label: 'Student / Schools Admin',
  tier: 'Partner',
  scope: 'School coordinator from a partner school.',
  can: ['Manage cohort roster', 'Bulk-invite student members', 'View cohort-level engagement (anonymized)', 'Coordinate sponsored bus access']
}, {
  icon: Building2,
  code: 'museum_admin',
  label: 'Museum Admin',
  tier: 'Partner',
  scope: 'Sikh Heritage Museum of Canada.',
  can: ['Process tax-receipt requests routed from sponsorships', 'Mark receipts issued / pending', 'Read-only access to sponsor records relevant to receipts', 'Cannot access member data outside this scope']
}, {
  icon: HeadphonesIcon,
  code: 'support',
  label: 'Support',
  tier: 'Staff',
  scope: 'Customer service.',
  can: ['View any member account (read-only)', 'Re-send invite codes and reset onboarding state', 'Escalate disputes to Treasury Admin', 'Cannot modify XP or collections']
}];
const UserRolesSection = () => <section id="roles" className="py-20 md:py-24 scroll-mt-20" data-id="element-7640">
    <SectionHeader num="01" eyebrow="Section One" title={<>
          User <span className="text-[#D4AF37]" data-id="element-7642">Roles</span>
        </>} intro="Every actor in the Treasury. Each role has a clear scope and a bounded set of permissions. Permissions are additive — a Misldar is also a Sardar." data-id="element-7641" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-7643">
      {ROLES.map(r => <Card key={r.code} data-id="element-7644">
          <div className="flex items-start justify-between mb-3 gap-3" data-id="element-7645">
            <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-7646">
              <div className="w-10 h-10 border border-[#D4AF37]/40 bg-black/40 flex items-center justify-center shrink-0" data-id="element-7647">
                <r.icon className="w-4 h-4 text-[#D4AF37]" data-id="element-7648" />
              </div>
              <div className="min-w-0" data-id="element-7649">
                <h4 className="font-serif text-lg text-[#FDFBF7] leading-tight" data-id="element-7650">
                  {r.label}
                </h4>
                <p className="font-mono text-[#D4AF37]/60 text-[10px] tracking-wider mt-0.5" data-id="element-7651">
                  {r.code}
                </p>
              </div>
            </div>
            <span className="text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]/80 border border-[#D4AF37]/30 px-1.5 py-0.5 bg-[#D4AF37]/5 shrink-0" data-id="element-7652">
              {r.tier}
            </span>
          </div>
          <p className="text-[#FDFBF7]/70 text-sm font-light leading-relaxed mb-3 italic" data-id="element-7653">
            {r.scope}
          </p>
          <ul className="space-y-1.5 mt-3 pt-3 border-t border-[#D4AF37]/10" data-id="element-7654">
            {r.can.map(c => <li key={c} className="text-[#FDFBF7]/60 text-xs font-light leading-relaxed flex items-start gap-2" data-id="element-7655">
                <span className="text-[#D4AF37]/60 mt-0.5" data-id="element-7656">·</span>
                <span data-id="element-7657">{c}</span>
              </li>)}
          </ul>
        </Card>)}
    </div>
  </section>;
// =====================================================================
// 2. CORE JOBS
// =====================================================================
const JOBS = [{
  title: 'Onboarding & Invite',
  actors: ['Member', 'Treasury Admin'],
  steps: ['Visitor requests invite (email) or arrives via ?ref code', 'System emails a 6-digit darbar seal', 'Visitor verifies code → creates court name + avatar', 'System creates user + member, status = sevadar', 'Founder packet issued (Founders\u2019 Edition flag if early enough)']
}, {
  title: 'Knighting Ceremony',
  actors: ['Sevadar', 'System'],
  steps: ['Sevadar meets readiness criteria (configurable)', 'Triggers Knighting modal — name + sword + oath', 'System awards +2,500 XP and writes ledger entry', 'Role upgraded sevadar → sardar', 'Trading and Misl-join unlocked']
}, {
  title: 'Daily Packet',
  actors: ['Member'],
  steps: ['Member opens daily packet (one per UTC day per member)', 'System rolls contents weighted by rarity + supply caps', 'Cards added to member_collection with duplicate tracking', 'Packet log row written; streak counter incremented', 'XP delta written to ledger']
}, {
  title: 'Trade',
  actors: ['Sender', 'Receiver'],
  steps: ['Sender selects cards offered + cards requested → submits', 'Trade row created, status = pending', 'Receiver sees trade in inbox, accepts or counters', 'On accept: atomic swap of ownership rows', 'Audit log written for both sides']
}, {
  title: 'Misl Lifecycle',
  actors: ['Misldar', 'Member'],
  steps: ['Knighted member creates a Misl (name, seal, oath)', 'Misldar invites members; invitees accept to join', 'Member may belong to one Misl at a time', 'Misldar can transfer leadership or disband', 'Founding-Misl status preserved for first 10 created']
}, {
  title: 'Reward Redemption',
  actors: ['Member'],
  steps: ['Member browses rewards tab; sees XP cost', 'Redeem → system checks XP balance and supply', 'Atomically decrements XP (ledger) and supply', 'Redemption row created, status = pending', 'Court Admin fulfills; status = fulfilled']
}, {
  title: 'Live Event Code',
  actors: ['Event Operator', 'Member'],
  steps: ['Event Operator generates code via on-site dashboard', 'Member scans / enters code in app', 'System validates: code valid, not used, in event window', 'Reward / artifact / XP issued', 'Redemption logged with member, code, timestamp']
}, {
  title: 'Treasury Night Drop',
  actors: ['Court Admin'],
  steps: ['Schedule drop: collection, supply, eligibility window', 'At drop time, eligible members notified', 'Drop opens → claims processed FIFO with supply lock', 'Sponsor attribution recorded if collection is sponsored', 'Drop closes; remaining supply rolled or burned']
}];
const CoreJobsSection = () => <section id="jobs" className="py-20 md:py-24 border-t border-[#D4AF37]/10 scroll-mt-20" data-id="element-7658">
    <SectionHeader num="02" eyebrow="Section Two" title={<>
          Core <span className="text-[#D4AF37]" data-id="element-7660">Jobs</span>
        </>} intro="The workflows the system has to handle end-to-end. Each is a discrete sequence the data model must support cleanly." data-id="element-7659" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-7661">
      {JOBS.map((j, i) => <Card key={j.title} data-id="element-7662">
          <div className="flex items-start gap-3 mb-4" data-id="element-7663">
            <span className="font-mono text-[#D4AF37]/50 text-xs shrink-0 mt-0.5" data-id="element-7664">
              J{String(i + 1).padStart(2, '0')}
            </span>
            <div data-id="element-7665">
              <h4 className="font-serif text-lg text-[#FDFBF7] leading-tight" data-id="element-7666">
                {j.title}
              </h4>
              <p className="text-[#D4AF37]/55 text-[10px] tracking-[0.2em] uppercase mt-1" data-id="element-7667">
                {j.actors.join(' · ')}
              </p>
            </div>
          </div>
          <ol className="space-y-1.5" data-id="element-7668">
            {j.steps.map((s, k) => <li key={k} className="text-[#FDFBF7]/65 text-xs sm:text-sm font-light leading-relaxed flex gap-3" data-id="element-7669">
                <span className="font-mono text-[#D4AF37]/55 shrink-0" data-id="element-7670">
                  {k + 1}.
                </span>
                <span data-id="element-7671">{s}</span>
              </li>)}
          </ol>
        </Card>)}
    </div>
  </section>;
// =====================================================================
// 3. BUSINESS RULES
// =====================================================================
const RULES = [{
  group: 'Identity & Access',
  items: ['One user account = one member record', 'Court name must be unique (case-insensitive)', 'Sevadar may not trade or join Misls', 'Role escalation is event-driven (Knighting, never manual edit)']
}, {
  group: 'Packets & Drops',
  items: ['Daily packet: one per member per UTC day', 'Rarity rolls are weighted; Founders\u2019 Edition supply is hard-capped', 'Sponsored cards must render sponsor mark; sponsorship is immutable post-issue', 'Treasury Night drops are FIFO with row-level supply lock']
}, {
  group: 'XP & Honour',
  items: ['XP is an append-only ledger — never overwritten', 'Honours / ranks are derived state (computed from ledger + rules)', 'Founding Misls (first 10) preserve status even if disbanded later', 'Knighting awards exactly +2,500 XP and writes a Knighting ledger entry']
}, {
  group: 'Trade Integrity',
  items: ['Both sides must accept before any swap', 'Card swap is a single DB transaction — never partial', 'A card can never be owned by two members simultaneously', 'Trades involving Founders\u2019 cards require review (flag = founder)']
}, {
  group: 'Codes & Events',
  items: ['Event codes are single-use unless explicitly marked re-issuable', 'Codes are valid only inside their declared time window', 'Live-event scans require an active event session ID', 'Code reuse attempts are logged but never error-silent']
}, {
  group: 'Sponsorship & Receipts',
  items: ['Sponsor attribution is set at collection creation and locked', 'Tax receipts are routed to Museum Admin — never issued by Treasury', 'Receipt-eligible records keep a separate redacted receipt_meta blob', 'Sponsor Admins see aggregates only, never PII']
}, {
  group: 'Schools / Cohorts',
  items: ['Student Admins manage rosters within their school only', 'Bulk-invited students start as sevadars', 'Sponsored bus access is granted via cohort-level entitlement', 'Cohort-level engagement is anonymized at the Student Admin view']
}, {
  group: 'Audit & Safety',
  items: ['All state-changing actions write to audit_log', 'Audit log entries are immutable', 'Soft-delete only for member-facing entities; hard-delete is admin-gated', 'Sensitive role changes require two-person approval']
}];
const BusinessRulesSection = () => <section id="rules" className="py-20 md:py-24 border-t border-[#D4AF37]/10 scroll-mt-20" data-id="element-7672">
    <SectionHeader num="03" eyebrow="Section Three" title={<>
          Business <span className="text-[#D4AF37]" data-id="element-7674">Rules</span>
        </>} intro="The invariants. These are the constraints the system must enforce regardless of UI, role, or surface. Break these and the world breaks." data-id="element-7673" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7675">
      {RULES.map(g => <Card key={g.group} data-id="element-7676">
          <div className="flex items-center gap-2 mb-4" data-id="element-7677">
            <Scale className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-7678" />
            <h4 className="font-serif text-base text-[#FDFBF7]" data-id="element-7679">{g.group}</h4>
          </div>
          <ul className="space-y-2" data-id="element-7680">
            {g.items.map(item => <li key={item} className="text-[#FDFBF7]/65 text-xs sm:text-sm font-light leading-relaxed flex items-start gap-2.5" data-id="element-7681">
                <span className="w-1 h-1 bg-[#D4AF37] rounded-full mt-1.5 shrink-0" data-id="element-7682" />
                <span data-id="element-7683">{item}</span>
              </li>)}
          </ul>
        </Card>)}
    </div>
  </section>;
// =====================================================================
// 4. DATA MODEL
// =====================================================================
type Col = {
  name: string;
  type: string;
  note?: string;
};
type Tbl = {
  table: string;
  group: string;
  cols: Col[];
};
const TABLES: Tbl[] = [
// Identity
{
  table: 'users',
  group: 'Identity',
  cols: [{
    name: 'id',
    type: 'uuid',
    note: 'pk'
  }, {
    name: 'email',
    type: 'text',
    note: 'unique'
  }, {
    name: 'auth_provider',
    type: 'text',
    note: 'email | google | apple'
  }, {
    name: 'created_at',
    type: 'timestamptz'
  }, {
    name: 'last_login_at',
    type: 'timestamptz'
  }]
}, {
  table: 'members',
  group: 'Identity',
  cols: [{
    name: 'id',
    type: 'uuid',
    note: 'pk'
  }, {
    name: 'user_id',
    type: 'uuid',
    note: 'fk → users.id, unique'
  }, {
    name: 'court_name',
    type: 'text',
    note: 'unique, ci'
  }, {
    name: 'avatar_key',
    type: 'text'
  }, {
    name: 'rank',
    type: 'enum',
    note: 'sevadar | sardar | misldar'
  }, {
    name: 'is_founder',
    type: 'bool',
    note: 'set at onboarding'
  }, {
    name: 'knighted_at',
    type: 'timestamptz',
    note: 'nullable'
  }, {
    name: 'created_at',
    type: 'timestamptz'
  }]
}, {
  table: 'role_grants',
  group: 'Identity',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'user_id',
    type: 'uuid',
    note: 'fk → users.id'
  }, {
    name: 'role',
    type: 'enum',
    note: 'see role codes in §1'
  }, {
    name: 'scope',
    type: 'jsonb',
    note: 'e.g. { school_id }'
  }, {
    name: 'granted_by',
    type: 'uuid'
  }, {
    name: 'granted_at',
    type: 'timestamptz'
  }, {
    name: 'revoked_at',
    type: 'timestamptz',
    note: 'nullable'
  }]
},
// Collections & Artifacts
{
  table: 'collections',
  group: 'Catalog',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'name',
    type: 'text'
  }, {
    name: 'sub_title',
    type: 'text'
  }, {
    name: 'rarity_tier',
    type: 'enum',
    note: 'founders | rare | epic | live | treasury'
  }, {
    name: 'is_flagship',
    type: 'bool'
  }, {
    name: 'sponsor_id',
    type: 'uuid',
    note: 'fk → sponsors, nullable, immutable'
  }, {
    name: 'total_artifacts',
    type: 'int'
  }, {
    name: 'unlock_order',
    type: 'int'
  }, {
    name: 'status',
    type: 'enum',
    note: 'draft | live | locked'
  }]
}, {
  table: 'artifacts',
  group: 'Catalog',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'collection_id',
    type: 'uuid',
    note: 'fk → collections'
  }, {
    name: 'name',
    type: 'text'
  }, {
    name: 'role_or_title',
    type: 'text'
  }, {
    name: 'image_url',
    type: 'text'
  }, {
    name: 'rarity',
    type: 'enum'
  }, {
    name: 'supply_cap',
    type: 'int',
    note: 'null = unlimited'
  }, {
    name: 'issued_count',
    type: 'int'
  }]
}, {
  table: 'member_artifacts',
  group: 'Catalog',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'member_id',
    type: 'uuid'
  }, {
    name: 'artifact_id',
    type: 'uuid'
  }, {
    name: 'acquired_at',
    type: 'timestamptz'
  }, {
    name: 'source',
    type: 'enum',
    note: 'packet | trade | event | reward'
  }, {
    name: 'is_duplicate',
    type: 'bool',
    note: 'derived flag'
  }]
},
// Misls
{
  table: 'misls',
  group: 'Misls',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'name',
    type: 'text',
    note: 'unique'
  }, {
    name: 'seal_image',
    type: 'text'
  }, {
    name: 'misldar_id',
    type: 'uuid',
    note: 'fk → members'
  }, {
    name: 'is_founding',
    type: 'bool',
    note: 'first 10 only'
  }, {
    name: 'created_at',
    type: 'timestamptz'
  }, {
    name: 'disbanded_at',
    type: 'timestamptz',
    note: 'nullable'
  }]
}, {
  table: 'misl_members',
  group: 'Misls',
  cols: [{
    name: 'misl_id',
    type: 'uuid'
  }, {
    name: 'member_id',
    type: 'uuid',
    note: 'unique — one Misl per member'
  }, {
    name: 'joined_at',
    type: 'timestamptz'
  }, {
    name: 'role',
    type: 'enum',
    note: 'misldar | member'
  }]
},
// Packets, Trades, Rewards
{
  table: 'packets',
  group: 'Activity',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'member_id',
    type: 'uuid'
  }, {
    name: 'opened_at',
    type: 'timestamptz',
    note: 'unique per member per UTC day'
  }, {
    name: 'contents',
    type: 'jsonb',
    note: 'array of artifact_ids'
  }, {
    name: 'streak_count',
    type: 'int'
  }]
}, {
  table: 'trades',
  group: 'Activity',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'sender_id',
    type: 'uuid'
  }, {
    name: 'receiver_id',
    type: 'uuid'
  }, {
    name: 'offered',
    type: 'jsonb',
    note: 'array of artifact_ids'
  }, {
    name: 'requested',
    type: 'jsonb'
  }, {
    name: 'message',
    type: 'text'
  }, {
    name: 'status',
    type: 'enum',
    note: 'pending | accepted | declined | cancelled'
  }, {
    name: 'requires_review',
    type: 'bool',
    note: 'founder cards'
  }]
}, {
  table: 'rewards',
  group: 'Activity',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'name',
    type: 'text'
  }, {
    name: 'xp_cost',
    type: 'int'
  }, {
    name: 'supply',
    type: 'int',
    note: 'null = unlimited'
  }, {
    name: 'category',
    type: 'enum',
    note: 'merch | event | sealed_chamber | physical'
  }, {
    name: 'is_active',
    type: 'bool'
  }]
}, {
  table: 'reward_redemptions',
  group: 'Activity',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'member_id',
    type: 'uuid'
  }, {
    name: 'reward_id',
    type: 'uuid'
  }, {
    name: 'status',
    type: 'enum',
    note: 'pending | fulfilled | cancelled'
  }, {
    name: 'redeemed_at',
    type: 'timestamptz'
  }, {
    name: 'fulfillment_meta',
    type: 'jsonb'
  }]
},
// Live events
{
  table: 'live_events',
  group: 'Events',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'kind',
    type: 'enum',
    note: 'experience | treasury_night | darbar_session'
  }, {
    name: 'starts_at',
    type: 'timestamptz'
  }, {
    name: 'ends_at',
    type: 'timestamptz'
  }, {
    name: 'venue',
    type: 'text'
  }]
}, {
  table: 'event_codes',
  group: 'Events',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'event_id',
    type: 'uuid'
  }, {
    name: 'code',
    type: 'text',
    note: 'unique'
  }, {
    name: 'is_reissuable',
    type: 'bool'
  }, {
    name: 'payload',
    type: 'jsonb',
    note: 'reward | artifact | xp'
  }, {
    name: 'window_start',
    type: 'timestamptz'
  }, {
    name: 'window_end',
    type: 'timestamptz'
  }]
}, {
  table: 'code_redemptions',
  group: 'Events',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'code_id',
    type: 'uuid'
  }, {
    name: 'member_id',
    type: 'uuid'
  }, {
    name: 'redeemed_at',
    type: 'timestamptz'
  }, {
    name: 'rejected_reason',
    type: 'text',
    note: 'nullable'
  }]
},
// Ledger & Audit
{
  table: 'xp_ledger',
  group: 'Ledger',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'member_id',
    type: 'uuid'
  }, {
    name: 'delta',
    type: 'int',
    note: 'positive or negative'
  }, {
    name: 'reason',
    type: 'enum',
    note: 'packet | trade | reward | knighting | event | admin_adjust'
  }, {
    name: 'ref_id',
    type: 'uuid',
    note: 'fk to source row'
  }, {
    name: 'created_at',
    type: 'timestamptz',
    note: 'append-only'
  }]
}, {
  table: 'honours',
  group: 'Ledger',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'member_id',
    type: 'uuid'
  }, {
    name: 'type',
    type: 'enum',
    note: 'order-of-rs | gold-medallion | silver-medallion | sangat-seal | royal-sash'
  }, {
    name: 'granted_at',
    type: 'timestamptz'
  }, {
    name: 'granted_by_role',
    type: 'text'
  }]
}, {
  table: 'audit_log',
  group: 'Ledger',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'actor_user_id',
    type: 'uuid'
  }, {
    name: 'action',
    type: 'text'
  }, {
    name: 'target_type',
    type: 'text'
  }, {
    name: 'target_id',
    type: 'uuid'
  }, {
    name: 'before',
    type: 'jsonb'
  }, {
    name: 'after',
    type: 'jsonb'
  }, {
    name: 'created_at',
    type: 'timestamptz'
  }]
},
// Sponsors & Schools
{
  table: 'sponsors',
  group: 'Partners',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'name',
    type: 'text'
  }, {
    name: 'kind',
    type: 'enum',
    note: 'corporate | family | individual'
  }, {
    name: 'contact_email',
    type: 'text'
  }]
}, {
  table: 'schools',
  group: 'Partners',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'name',
    type: 'text'
  }, {
    name: 'district',
    type: 'text'
  }, {
    name: 'student_admin_user_id',
    type: 'uuid'
  }]
}, {
  table: 'cohort_memberships',
  group: 'Partners',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'school_id',
    type: 'uuid'
  }, {
    name: 'member_id',
    type: 'uuid'
  }, {
    name: 'grade',
    type: 'text'
  }, {
    name: 'bus_sponsorship_id',
    type: 'uuid',
    note: 'nullable'
  }]
}, {
  table: 'bus_sponsorships',
  group: 'Partners',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'sponsor_id',
    type: 'uuid'
  }, {
    name: 'amount_pledged',
    type: 'numeric'
  }, {
    name: 'amount_paid',
    type: 'numeric'
  }, {
    name: 'plan',
    type: 'enum',
    note: 'full | installments'
  }, {
    name: 'tax_receipt_requested',
    type: 'bool'
  }, {
    name: 'museum_receipt_status',
    type: 'enum',
    note: 'pending | issued | not_applicable'
  }, {
    name: 'recognition_pref',
    type: 'enum'
  }]
},
// Invites
{
  table: 'invites',
  group: 'Identity',
  cols: [{
    name: 'id',
    type: 'uuid'
  }, {
    name: 'from_member_id',
    type: 'uuid',
    note: 'nullable'
  }, {
    name: 'code',
    type: 'text',
    note: 'unique'
  }, {
    name: 'misl_id',
    type: 'uuid',
    note: 'nullable'
  }, {
    name: 'used_by_user_id',
    type: 'uuid',
    note: 'nullable'
  }, {
    name: 'expires_at',
    type: 'timestamptz'
  }]
}];
const TABLE_GROUPS = ['Identity', 'Catalog', 'Misls', 'Activity', 'Events', 'Ledger', 'Partners'] as const;
const DataModelSection = () => {
  const [activeGroup, setActiveGroup] = useState<string>('all');
  const filtered = activeGroup === 'all' ? TABLES : TABLES.filter(t => t.group === activeGroup);
  return <section id="model" className="py-20 md:py-24 border-t border-[#D4AF37]/10 scroll-mt-20" data-id="element-7684">
      <SectionHeader num="04" eyebrow="Section Four" title={<>
            Data <span className="text-[#D4AF37]" data-id="element-7686">Model</span>
          </>} intro="The boring tables. Before any AI, before any rendering layer — these are the structures that persist the world. Postgres-shaped, but DB-agnostic." data-id="element-7685" />

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-8" data-id="element-7687">
        <button onClick={() => setActiveGroup('all')} className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 border transition-colors ${activeGroup === 'all' ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10 font-bold' : 'border-[#D4AF37]/20 text-[#FDFBF7]/55 hover:border-[#D4AF37]/45'}`} data-id="element-7688">
          All ({TABLES.length})
        </button>
        {TABLE_GROUPS.map(g => {
        const count = TABLES.filter(t => t.group === g).length;
        return <button key={g} onClick={() => setActiveGroup(g)} className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 border transition-colors ${activeGroup === g ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10 font-bold' : 'border-[#D4AF37]/20 text-[#FDFBF7]/55 hover:border-[#D4AF37]/45'}`} data-id="element-7689">
              {g} ({count})
            </button>;
      })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-7690">
        {filtered.map(t => <Card key={t.table} accent={t.group === 'Ledger'} data-id="element-7691">
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#D4AF37]/15" data-id="element-7692">
              <div className="flex items-center gap-2.5" data-id="element-7693">
                <Table className="w-3.5 h-3.5 text-[#D4AF37]/60" data-id="element-7694" />
                <code className="font-mono text-[#D4AF37] text-sm" data-id="element-7695">
                  {t.table}
                </code>
              </div>
              <span className="text-[9px] tracking-[0.25em] uppercase text-[#FDFBF7]/40" data-id="element-7696">
                {t.group}
              </span>
            </div>
            <div className="space-y-1" data-id="element-7697">
              {t.cols.map(c => <div key={c.name} className="flex items-baseline gap-3 text-xs font-mono" data-id="element-7698">
                  <span className="text-[#FDFBF7]/80 min-w-[140px] shrink-0" data-id="element-7699">
                    {c.name}
                  </span>
                  <span className="text-[#D4AF37]/70 shrink-0" data-id="element-7700">{c.type}</span>
                  {c.note && <span className="text-[#FDFBF7]/35 text-[11px] italic truncate" data-id="element-7701">
                      &#47;&#47; {c.note}
                    </span>}
                </div>)}
            </div>
          </Card>)}
      </div>
    </section>;
};
// =====================================================================
// HERO
// =====================================================================
const BlueprintHero = () => <section className="relative pt-24 pb-12 border-b border-[#D4AF37]/15" data-id="element-7702">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.08),_transparent_60%)] pointer-events-none" data-id="element-7703" />
    <div className="relative z-10" data-id="element-7704">
      <Link to="/treasure" className="inline-flex items-center gap-2 text-[#D4AF37]/60 hover:text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-8 transition-colors" data-id="element-7705">
        <ChevronLeft className="w-3.5 h-3.5" data-id="element-7706" />
        Back to Treasury
      </Link>
      <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-[#D4AF37]/40 bg-[#D4AF37]/10 mb-5" data-id="element-7707">
        <Lock className="w-3 h-3 text-[#D4AF37]" data-id="element-7708" />
        <span className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-7709">
          Sealed Chamber · Internal Blueprint
        </span>
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.05] tracking-tight mb-5" data-id="element-7710">
        The Treasury, <span className="text-[#D4AF37]" data-id="element-7711">Mapped</span>
      </h1>
      <p className="text-[#FDFBF7]/65 text-base sm:text-lg font-light leading-relaxed max-w-3xl mb-8" data-id="element-7712">
        Roles. Jobs. Rules. Tables. Everything that has to be true under the
        surface before any AI, any UI, any drama. This is the boring document
        that makes the magic possible.
      </p>

      {/* Section index */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 max-w-3xl" data-id="element-7713">
        {[{
        num: '01',
        label: 'User Roles',
        count: `${ROLES.length}`,
        href: '#roles',
        icon: Users
      }, {
        num: '02',
        label: 'Core Jobs',
        count: `${JOBS.length}`,
        href: '#jobs',
        icon: Workflow
      }, {
        num: '03',
        label: 'Business Rules',
        count: `${RULES.reduce((s, g) => s + g.items.length, 0)}`,
        href: '#rules',
        icon: Scale
      }, {
        num: '04',
        label: 'Data Model',
        count: `${TABLES.length}`,
        href: '#model',
        icon: Database
      }].map(s => <a key={s.num} href={s.href} className="group border border-[#D4AF37]/15 hover:border-[#D4AF37]/55 bg-black/40 px-4 py-3 transition-colors flex items-center gap-3" data-id="element-7714">
            <s.icon className="w-3.5 h-3.5 text-[#D4AF37]/55 group-hover:text-[#D4AF37] transition-colors" data-id="element-7715" />
            <div className="flex-1 min-w-0" data-id="element-7716">
              <p className="font-mono text-[#D4AF37]/50 text-[10px] tracking-[0.2em]" data-id="element-7717">
                §{s.num}
              </p>
              <p className="font-serif text-[#FDFBF7] text-sm leading-tight truncate" data-id="element-7718">
                {s.label}
              </p>
            </div>
            <span className="text-[#D4AF37]/70 text-[10px] font-mono shrink-0" data-id="element-7719">
              {s.count}
            </span>
          </a>)}
      </div>
    </div>
  </section>;
// =====================================================================
// PAGE
// =====================================================================
export const TreasuryBlueprintPage = () => <div className="bg-[#0B0F19] min-h-screen text-[#FDFBF7] selection:bg-[#D4AF37]/30" data-id="element-7720">
    {/* Top strip */}
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0F19]/90 border-b border-[#D4AF37]/20" data-id="element-7721">
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between" data-id="element-7722">
        <Link to="/treasure" className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase flex items-center gap-1 transition-colors" data-id="element-7723">
          <ChevronLeft className="w-4 h-4" data-id="element-7724" /> Treasury
        </Link>
        <div className="flex items-center gap-2 font-serif text-xs sm:text-sm text-[#D4AF37] tracking-[0.25em] uppercase" data-id="element-7725">
          <Key className="w-3 h-3" data-id="element-7726" />
          Sealed Chamber
        </div>
        <div className="w-16" data-id="element-7727" />
      </div>
    </header>

    <main className="max-w-6xl mx-auto px-4" data-id="element-7728">
      <BlueprintHero data-id="element-7729" />
      <UserRolesSection data-id="element-7730" />
      <CoreJobsSection data-id="element-7731" />
      <BusinessRulesSection data-id="element-7732" />
      <DataModelSection data-id="element-7733" />

      {/* Footer note */}
      <div className="py-16 border-t border-[#D4AF37]/15 text-center" data-id="element-7734">
        <p className="text-[#D4AF37]/50 text-[10px] tracking-[0.3em] uppercase mb-3 font-light flex items-center justify-center gap-2" data-id="element-7735">
          <EyeOff className="w-3 h-3" data-id="element-7736" />
          Internal · Not Linked Publicly
        </p>
        <p className="text-[#FDFBF7]/40 text-xs font-light italic" data-id="element-7737">
          &ldquo;Before the court, the ledger.&rdquo;
        </p>
      </div>
    </main>
  </div>;